import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SortDownArrow } from "../../assets/svg";
import Action from "../../components/common/Action";
import Toggles from "../../components/common/Toggles";
import Breadcrumb from "../../components/common/BreadCrumb";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customTable/CustomTable";
import RippleLoader from "../../components/loader/RippleLoader";
import { AddButton } from "../../components/micro/input";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteProduct,
  getProductList,
  updateProductStatus,
} from "../../redux/product/slice";
import { getCategoriesList } from "../../redux/categories/slice";
import ReactSelect from "react-select";

const Product = () => {
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [allCategoriesListArray, setAllCategoriesListArray] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(null);
  const [productDelete, setProductDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // selector
  const {
    productsList,
    productsListLoading,
    allCategoriesList,
    allCategoriesLoading,
  } = useSelector((store) => ({
    productsList: store?.productsData?.getProductListStatus?.data,
    productsListLoading: store?.productsData?.loading,
    // all RoleL ist
    allCategoriesList: store?.categoriesData?.getCategoriesListStatus?.data,
    allCategoriesLoading: store?.categoriesData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getProductList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        category_id: selectedCategories?.value?.id,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  }, [
    dispatch,
    searchValue,
    pageCount,
    pageLimit,
    selectedCategories,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    dispatch(getCategoriesList({ search: "", limit: "all" }));
  }, []);

  useEffect(() => {
    if (allCategoriesList?.data && Array.isArray(allCategoriesList?.data)) {
      const options = allCategoriesList?.data?.map((x) => ({
        label: x.name,
        value: x,
      }));
      setAllCategoriesListArray(options);
    } else {
      setAllCategoriesListArray([]);
    }
  }, [allCategoriesList]);

  const updateStatus = (element, isActive) => {
    dispatch(
      updateProductStatus({
        id: element?.id,
        data: {
          is_active: isActive,
        },
      })
    ).then((res) => {
      if (res.type === "updateProductStatus/fulfilled") {
        dispatch(
          getProductList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            category_id: selectedCategories?.value?.id,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
      }
    });
  };

  const deleteCategoryId = () => {
    setIsLoading(true);
    if (!productDelete) return;
    dispatch(deleteProduct(productDelete)).then((res) => {
      if (res.type === "deleteProduct/fulfilled") {
        dispatch(
          getProductList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            category_id: selectedCategories?.value?.id,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
      }
      setIsLoading(false);
      setConfirmDelete(false);
    });
  };

  const handleToggle = (element) => {
    const isActive = element.is_active !== 1;
    updateStatus(element, isActive);
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        ID
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Product</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "name" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("category_name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Category</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "category_name"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Pricing
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div className="flex items-center">
          <span>Status</span>
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Action
      </th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(productsList?.data?.data || [])?.map((element, index) => (
        <tr
          key={element.id}
          className="font-Proxima shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="px-5 py-3 truncate">{element?.id}</td>
          <td className="px-5 py-3 truncate">
            {element?.name
              ? element?.name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3 truncate">
            {element?.category_name
              ? element?.category_name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3 truncate">
            ${element?.price ? element.price.toFixed(2) : "0.00"}
          </td>
          <td className="px-5 py-3 truncate">
            <Toggles
              checked={element?.is_active === 1}
              toggleFor={element?.id}
              onChange={() => handleToggle(element)}
            />
          </td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/create-edit-product", {
                        state: { product: element, mode: "edit" },
                      })
                    }
                  >
                    View/Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setProductDelete(element?.id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-7">
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              Product
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={"Product"}
              breadCrumbParent2={"Product Management"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
          <AddButton
            value="Add Product"
            onClick={() =>
              navigate("/create-edit-product", {
                state: { mode: "create" },
              })
            }
          />
        </div>
        <div className="flex items-center space-x-3">
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="category"
            placeholder="Category"
            options={
              Array.isArray(allCategoriesListArray)
                ? allCategoriesListArray
                : []
            }
            isClearable={true}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            value={selectedCategories}
            onChange={(e) => {
              setSelectedCategories(e);
            }}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderColor: selectedCategories ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <DebounceSearch
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
        </div>
        {productsListLoading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg">
            <CustomTable
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={productsList?.data}
              overflowVisibleLg={true}
              // loader
              loading={false}
              showPagination={true}
              // showLimit
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              // paginationData
              total={productsList?.data?.total}
              current={pageCount}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(productsList?.data?.total / pageLimit)}
            />
          </div>
        )}
      </div>
      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteCategoryId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default Product;
